import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { frontendDb as db } from '../lib/frontend-db';
import type { ContactFormSettings } from '../lib/frontend-db';

const ContactFormSection = () => {
  const [settings, setSettings] = useState<ContactFormSettings | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    number_value: '',
    message: ''
  });
  const [showCustomNumber, setShowCustomNumber] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await db.getContactFormSettings() as ContactFormSettings | null;
      setSettings(data);
    } catch (error) {
      console.error('Error loading contact form settings:', error);
    }
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    
    // Validiere je nach field_type
    if (settings?.field_type === 'subject') {
      if (!formData.subject.trim()) {
        newErrors.subject = 'Betreff ist erforderlich';
      }
    } else if (settings?.field_type === 'number') {
      if (!formData.number_value || formData.number_value === '' || formData.number_value === 'custom') {
        newErrors.number_value = 'Bitte wählen Sie eine Anzahl oder geben Sie eine ein';
      } else {
        const num = parseInt(formData.number_value);
        if (isNaN(num) || num < 1 || num > 999) {
          newErrors.number_value = 'Anzahl muss zwischen 1 und 999 liegen';
        }
      }
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Nachricht ist erforderlich';
    }
    
    if (!privacyAccepted) {
      newErrors.privacy = 'Sie müssen die Datenschutzerklärung akzeptieren';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      console.log('📤 Submitting contact form:', formData);
      const result = await db.submitContactForm(formData) as any;
      console.log('✅ Contact form submitted successfully:', result);
      
      setStatus('success');
      setMessage(settings?.success_message || 'Ihre Nachricht wurde erfolgreich gesendet!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        number_value: '',
        message: ''
      });
      setShowCustomNumber(false);
      setErrors({});
      setPrivacyAccepted(false);
    } catch (error) {
      console.error('❌ Error submitting contact form:', error);
      setStatus('error');
      const errorMessage = (error as Error).message || 'Unbekannter Fehler';
      setMessage(settings?.error_message || `Es ist ein Fehler aufgetreten: ${errorMessage}. Bitte versuchen Sie es später erneut.`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (!settings || !settings.enabled) {
    return null;
  }

  const bgColor = settings.background_color === 'transparent' 
    ? 'var(--bg-secondary)' 
    : settings.background_color;
  const textColor = settings.text_color === 'inherit' 
    ? 'var(--text-primary)' 
    : settings.text_color;

  return (
    <section id="contact-form" className="py-8 sm:py-16 transition-colors" style={{ backgroundColor: bgColor }}>
      <div className="container mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--primary-color)] mb-6 sm:mb-8 text-center">
          {settings.section_title}
        </h2>
        
        <div className="max-w-2xl mx-auto">
          <div className="rounded-lg shadow-lg p-4 sm:p-6 lg:p-8" style={{ backgroundColor: 'var(--bg-primary)' }}>
            <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-center" style={{ color: textColor }}>
              {settings.form_title}
            </h3>
            
            {settings.form_description && (
              <p className="text-sm sm:text-base text-center mb-4 sm:mb-6 px-2" style={{ color: textColor }}>
                {settings.form_description}
              </p>
            )}
            
            {status === 'success' && (
              <div className="mb-4 sm:mb-6 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-green-800 text-center text-sm sm:text-base">
                {message}
              </div>
            )}
            
            {status === 'error' && (
              <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 text-red-800 text-center text-sm sm:text-base">
                {message}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-sm font-medium mb-1 sm:mb-2"
                  style={{ color: textColor }}
                >
                  {settings.name_label} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] text-base ${
                    errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={loading}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium mb-1 sm:mb-2"
                  style={{ color: textColor }}
                >
                  {settings.email_label} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] text-base ${
                    errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={loading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              
              {/* Betreff oder Anzahl - je nach Einstellung */}
              {settings.field_type === 'subject' ? (
                <div>
                  <label 
                    htmlFor="subject" 
                    className="block text-sm font-medium mb-1 sm:mb-2"
                    style={{ color: textColor }}
                  >
                    {settings.subject_label} *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] text-base ${
                      errors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    disabled={loading}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>
              ) : (
                <div>
                  <label 
                    htmlFor="number_value" 
                    className="block text-sm font-medium mb-1 sm:mb-2"
                    style={{ color: textColor }}
                  >
                    {settings.number_label} *
                  </label>
                  
                  {!showCustomNumber ? (
                    <select
                      id="number_value"
                      value={formData.number_value || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'custom') {
                          setShowCustomNumber(true);
                          setFormData({ ...formData, number_value: '' });
                        } else {
                          setFormData({ ...formData, number_value: val });
                          if (errors.number_value) {
                            setErrors({ ...errors, number_value: '' });
                          }
                        }
                      }}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] text-base ${
                        errors.number_value ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      disabled={loading}
                    >
                      <option value="">Bitte wählen Sie...</option>
                      {[...Array(20)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} {i + 1 === 1 ? 'Person' : 'Personen'}
                        </option>
                      ))}
                      <option value="custom">Andere Anzahl eingeben...</option>
                    </select>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="number"
                        value={formData.number_value}
                        onChange={(e) => {
                          setFormData({ ...formData, number_value: e.target.value });
                          if (errors.number_value) {
                            setErrors({ ...errors, number_value: '' });
                          }
                        }}
                        min="1"
                        max="999"
                        placeholder="Anzahl eingeben (z.B. 25)"
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] text-base ${
                          errors.number_value ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        disabled={loading}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomNumber(false);
                          setFormData({ ...formData, number_value: '' });
                        }}
                        className="text-sm text-[var(--primary-color)] hover:underline touch-manipulation"
                      >
                        ← Zurück zur Auswahl
                      </button>
                    </div>
                  )}
                  
                  {errors.number_value && (
                    <p className="mt-1 text-sm text-red-600">{errors.number_value}</p>
                  )}
                </div>
              )}
              
              <div>
                <label 
                  htmlFor="message" 
                  className="block text-sm font-medium mb-1 sm:mb-2"
                  style={{ color: textColor }}
                >
                  {settings.message_label} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] resize-vertical text-base ${
                    errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={loading}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <input
                  type="checkbox"
                  id="privacy-accept"
                  checked={privacyAccepted}
                  onChange={(e) => {
                    setPrivacyAccepted(e.target.checked);
                    if (errors.privacy) {
                      setErrors({ ...errors, privacy: '' });
                    }
                  }}
                  className="mt-1 h-4 w-4 sm:h-5 sm:w-5 rounded border-gray-300 text-[var(--primary-color)] focus:ring-[var(--primary-color)] touch-manipulation"
                  disabled={loading}
                />
                <label htmlFor="privacy-accept" className="text-sm leading-relaxed" style={{ color: textColor }}>
                  Ich akzeptiere die <Link to="/datenschutz" className="text-[var(--primary-color)] underline hover:opacity-80" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</Link>
                </label>
              </div>
              {errors.privacy && (
                <p className="text-sm text-red-600">{errors.privacy}</p>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--primary-color)] text-white font-semibold py-3 sm:py-4 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg touch-manipulation min-h-[48px]"
              >
                {loading ? 'Wird gesendet...' : settings.submit_button_text}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;


